import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, CreditCard, Loader2, Repeat, RotateCcw } from 'lucide-react';
import type { Order, OrderStatus } from '@bloom/shared';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { getDisplayCategory } from '../../lib/dtpCategories';
import { trackStoreEvent } from '../../lib/dtpAnalytics';
import { track } from '../../lib/analytics';
import { doseFamilyKey, useDtpCartStore } from '../../store/useDtpCartStore';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import type { PortalOrder } from '../../types/refills';

/**
 * Shared pieces of the portal's order views (home and Orders): where an order
 * is on its way to the patient, "Buy again", and "Pay now" for an order that
 * waits on the patient's payment.
 */

const STEPS = ['Placed', 'Preparing', 'Shipped', 'Delivered'] as const;

type Tone = 'neutral' | 'progress' | 'done' | 'problem';
const STAGE: Record<OrderStatus, { label: string; step: number; tone: Tone }> = {
  draft: { label: 'Draft', step: 0, tone: 'neutral' },
  awaiting_payment: { label: 'Awaiting payment', step: 0, tone: 'neutral' },
  submitted: { label: 'Placed', step: 0, tone: 'progress' },
  processing: { label: 'Preparing', step: 1, tone: 'progress' },
  shipped: { label: 'On its way', step: 2, tone: 'progress' },
  delivered: { label: 'Delivered', step: 3, tone: 'done' },
  cancelled: { label: 'Cancelled', step: -1, tone: 'problem' },
  voided: { label: 'Voided', step: -1, tone: 'problem' },
  failed: { label: 'Failed', step: -1, tone: 'problem' },
};

export function orderStage(status: string) {
  return STAGE[status as OrderStatus] || { label: status.replace(/_/g, ' '), step: 0, tone: 'neutral' as Tone };
}

const PILL: Record<Tone, string> = {
  neutral: 'bg-white/80 text-[var(--portal-muted)] ring-[var(--portal-line)]',
  progress: 'bg-[var(--portal-tint-1)] text-[var(--portal-ink)] ring-transparent',
  done: 'bg-[#dcf2e3] text-[#14532d] ring-transparent',
  problem: 'bg-[#fde4dc] text-[#8c2a14] ring-transparent',
};

export function OrderStatusPill({ status }: { status: string }) {
  const stage = orderStage(status);
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${PILL[stage.tone]}`}>
    {stage.tone === 'progress' && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current opacity-70 motion-safe:animate-pulse" />}
    {stage.label}
  </span>;
}

/** Placed → Preparing → Shipped → Delivered. Nothing for an order that left the track. */
export function OrderProgress({ status }: { status: string }) {
  const { step } = orderStage(status);
  if (step < 0) return null;
  return <ol aria-label={`Order progress: ${orderStage(status).label}`} className="grid grid-cols-4 gap-1.5">
    {STEPS.map((label, index) => {
      const reached = index <= step;
      return <li key={label} aria-current={index === step ? 'step' : undefined} className="min-w-0">
        <span aria-hidden="true" className={`block h-1.5 rounded-full transition-colors ${reached ? 'bg-[var(--portal-accent)]' : 'bg-[rgba(36,26,21,0.08)]'}`} />
        <span className={`mt-2 block truncate text-[11px] font-medium sm:text-xs ${reached ? 'text-[var(--portal-ink)]' : 'text-[var(--portal-muted)]'}`}>{label}</span>
      </li>;
    })}
  </ol>;
}

/**
 * Add catalog products to the cart at today's prices, under the shop's rules:
 * only what the clinic still sells, and at most one weight-loss product per
 * cart. Returns how many of the products are now in the cart. Checkout then
 * re-checks eligibility, consent and stock as for any order.
 */
export async function addProductsToCart(physicianId: string, productIds: string[], source: string): Promise<number> {
  const res = await dtpApi.getCatalog(physicianId);
  if (!res.success || !res.data) throw new Error('Catalog unavailable');
  const products = new Map(res.data.products.map(product => [product._id, product]));
  const isWeightLoss = (id: string) => getDisplayCategory(products.get(id)?.categories || []) === 'Weight Loss';
  useDtpCartStore.getState().applyClinicFromCatalog(res.data.clinic);
  let added = 0;
  for (const id of productIds) {
    const product = products.get(String(id));
    if (!product) continue;
    const { items, hasItem, addItem } = useDtpCartStore.getState();
    if (hasItem(product._id)) { added++; continue; }
    const family = doseFamilyKey(product);
    // Another dose of the same drug is replaced by addItem, so it doesn't count
    // against the one-weight-loss-product rule.
    if (isWeightLoss(product._id) && items.some(i => isWeightLoss(i.productId) && !(family && i.family === family))) continue;
    addItem({ productId: product._id, productName: product.displayName, price: product.price, family });
    trackStoreEvent('add_to_cart', { productId: product._id, source });
    added++;
  }
  return added;
}

/**
 * Put a past order's products back in the cart at today's prices and go to
 * checkout. Products the clinic no longer offers are left out, and so is a
 * second weight-loss product (the shop's one-per-cart rule).
 */
export function useBuyAgain() {
  const { physicianId } = useDtpParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function buyAgain(order: Pick<Order, 'items'> & { _id?: string }) {
    if (!physicianId) return;
    setBusy(true); setMessage('');
    try {
      const productIds = (order.items || []).map(item => String(item.productId));
      const added = await addProductsToCart(physicianId, productIds, 'portal_reorder');
      track('buy_again_clicked', { orderId: order._id, added, skipped: productIds.length - added });
      if (added) navigate(dtpPath(physicianId, '/checkout'));
      else setMessage('These items aren’t available to order online right now. Contact your clinic to reorder.');
    } catch {
      setMessage('We couldn’t start your reorder. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  return { buyAgain, busy, message };
}

export function BuyAgainButton({ order, variant = 'solid' }: { order: Pick<Order, 'items'> & { _id?: string }; variant?: 'solid' | 'soft' }) {
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const { buyAgain, busy, message } = useBuyAgain();
  const inCart = useDtpCartStore(s => (order.items || []).length > 0 && (order.items || []).every(item => s.items.some(i => i.productId === String(item.productId))));
  // Purchases are disabled while a clinic views the portal.
  if (impersonated || !order.items?.length) return null;
  return <div className="flex flex-col items-start gap-2">
    <button type="button" onClick={() => buyAgain(order)} disabled={busy}
      className={`inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(224,86,58,0.25)] disabled:opacity-60 ${variant === 'solid' ? 'bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] hover:opacity-90' : 'bg-white/80 text-[var(--portal-ink)] ring-1 ring-[var(--portal-line)] hover:bg-white'}`}>
      {busy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : inCart ? <Check aria-hidden="true" className="h-4 w-4" /> : <RotateCcw aria-hidden="true" className="h-4 w-4" />}
      {inCart ? 'In your cart' : 'Buy again'}
    </button>
    {message && <p role="alert" className="text-sm text-[#8c2a14]">{message}</p>}
  </div>;
}

/**
 * "Pay now" for an order waiting on the patient's card (an auto-refill, or an
 * order the clinic prepared). Opens the same payment page the texted link
 * does; the server only sends payUrl while the order can actually be paid.
 */
export function PayNowButton({ order, className = '' }: { order: Pick<PortalOrder, 'payUrl' | 'totalAmount'> & { _id?: string; refill?: PortalOrder['refill'] }; className?: string }) {
  const impersonated = useDtpAuthStore(s => s.impersonated);
  // Paying is the patient's own action, as with Buy again.
  if (impersonated || !order.payUrl) return null;
  return <a href={order.payUrl} onClick={() => track('pay_now_clicked', { orderId: order._id, isRefill: !!order.refill })}
    className={`inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-5 text-sm font-semibold text-[var(--portal-primary-fg)] transition hover:opacity-90 focus:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(224,86,58,0.25)] ${className}`}>
    <CreditCard aria-hidden="true" className="h-4 w-4" />Pay now{Number.isFinite(order.totalAmount) ? ` · ${formatMoney(order.totalAmount)}` : ''}
  </a>;
}

/** Banner for the top of an order card while it awaits payment. */
export function PayNowBanner({ order }: { order: PortalOrder }) {
  if (!order.payUrl) return null;
  return <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[var(--portal-tint-1)] p-4">
    <p className="min-w-0 text-[15px]">
      <span className="font-semibold">{order.refill ? 'Your refill is ready.' : 'This order is ready.'}</span>{' '}
      <span className="text-[var(--portal-muted)]">It ships once you pay. Nothing is charged until you do.</span>
    </p>
    <PayNowButton order={order} />
  </div>;
}

/** Marks an order an auto-refill prepared. */
export function RefillTag({ order }: { order: Pick<PortalOrder, 'refill'> }) {
  if (!order.refill) return null;
  return <span className="inline-flex items-center gap-1 rounded-full bg-[var(--portal-tint-3)] px-2.5 py-1 text-xs font-semibold text-[var(--portal-ink)]">
    <Repeat aria-hidden="true" className="h-3.5 w-3.5" />Auto-refill
  </span>;
}

export function formatMoney(value?: number) {
  return Number.isFinite(value) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value!) : '—';
}
